/**
 * Job Posting Preview — read-only view of an insights-tracked posting.
 *
 * Lives at /applications/preview/:companyDomain/:jobId. Used as the click
 * destination from the Open Positions table on JobFamilyPage. No Application
 * record is created on view — clicking "Start Application" is the bridge
 * to the existing ApplicationDetailPage flow.
 *
 * Per-requirement profile-fit (dot meter + matched evidence) uses sample data
 * for now — labelled with a "Sample match" pill. The "I have this" CTA hits
 * the real conversation flow at /api/profile/enhancement/conversations.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaceIcon from '@mui/icons-material/Place';
import PaidIcon from '@mui/icons-material/Paid';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import StairsIcon from '@mui/icons-material/Stairs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import { SAGE, CREAM } from '../theme';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';
import RequirementRow, { TargetDots } from '../components/jobs/RequirementRow';
import ScopeBulletsModal from '../components/enhancement/ScopeBulletsModal';
import FillTheGapModal from '../components/enhancement/FillTheGapModal';

// `about` label is dynamically tweaked to "About {Company}" at render time
const BASE_TABS = [
  { id: 'requirements', label: "Who they're looking for" },
  { id: 'responsibilities', label: "What you'll do" },
  { id: 'about', label: 'About' },
  { id: 'raw', label: 'Full Posting' },
];

// Themes for the position-level fit summary tile. Each tile's dot count is
// the *bar this role sets*, not a match score — so dots represent how
// demanding the role is on that axis. Profile-vs-bar comparison is a later
// pass. Attenuation rules per axis live in computeThemeBars below.

// ---- Theme attenuation ---------------------------------------------------
// Edu: 1 = none mentioned, 2 = Bachelor's, 3 = Master's/MBA, 4 = PhD/JD/MD.
//      +1 if a specific focus area is named (e.g. "PhD in Statistics").
const eduDots = (reqsText) => {
  if (!reqsText) return 1;
  const t = reqsText.toLowerCase();
  let bar = 1;
  if (/\b(bachelor|ba\b|bs\b|b\.s\.|b\.a\.|undergraduate degree)\b/.test(t)) bar = 2;
  if (/\b(master|m\.s\.|m\.a\.|ms\b|ma\b|mba|graduate degree)\b/.test(t)) bar = Math.max(bar, 3);
  if (/\b(ph\.?d|doctorate|jd\b|j\.d\.|md\b|m\.d\.)\b/.test(t)) bar = Math.max(bar, 4);
  // Focus area bump: matches "degree in X", "PhD in X", "Master's in X"
  if (bar >= 2 && /\b(bachelor'?s?|master'?s?|mba|ph\.?d|doctorate|degree)\s+in\s+[a-z]/.test(t)) {
    bar = Math.min(5, bar + 1);
  }
  return bar;
};

// Exp: scale by seniority. Falls through level → experience_level → 2 (mid).
const expDots = (level, experienceLevel) => {
  const s = `${level || ''} ${experienceLevel || ''}`.toLowerCase();
  if (/\b(director|vp\b|head of|chief|principal)\b/.test(s)) return 5;
  if (/\b(staff|manager|lead)\b/.test(s)) return 4;
  if (/\b(senior|sr\.?)\b/.test(s)) return 3;
  if (/\b(junior|jr\.?|associate|entry|intern)\b/.test(s)) return 1;
  return 2; // mid / unspecified
};

// Tech: base by job_family, +1 if requirements mention 3+ specific tools.
const techDots = (jobFamily, reqsText) => {
  const fam = (jobFamily || '').toLowerCase();
  let bar;
  if (/(engineer|developer|scientist|sre|devops|ml|data|infra|architect|security)/.test(fam)) bar = 4;
  else if (/(product|design|analyst|research|technical program)/.test(fam)) bar = 3;
  else if (/(sales|market|recruit|operation|finance|legal|people|hr)/.test(fam)) bar = 2;
  else bar = 2;
  // Tool/tech keyword count bump
  const t = (reqsText || '').toLowerCase();
  const hits = [
    /\bpython\b/, /\bjava\b/, /\bgolang|\bgo\b/, /\brust\b/, /\bc\+\+/,
    /\baws\b|\bgcp\b|\bazure\b/, /\bkubernetes|k8s\b/, /\bdocker\b/,
    /\bsql\b/, /\bspark\b/, /\bairflow\b/, /\bdbt\b/, /\bsnowflake\b/,
    /\bpytorch|tensorflow|scikit/, /\breact\b|\bvue\b|\bangular\b/, /\btypescript\b/,
  ].filter((r) => r.test(t)).length;
  if (hits >= 3) bar = Math.min(5, bar + 1);
  return bar;
};

// Mgmt: base by level, +1 if explicit leadership keywords in reqs/quals.
const mgmtDots = (level, experienceLevel, reqsText) => {
  const s = `${level || ''} ${experienceLevel || ''}`.toLowerCase();
  let bar = 1;
  if (/\b(director|vp\b|head of|chief)\b/.test(s)) bar = 4;
  else if (/\b(manager|lead)\b/.test(s)) bar = 3;
  else if (/\b(staff|principal)\b/.test(s)) bar = 2;
  else if (/\b(senior|sr\.?)\b/.test(s)) bar = 2;
  const t = (reqsText || '').toLowerCase();
  if (/\b(manage|mentor|lead a team|direct reports|managing|managed|people manager|team of \d|coaching)\b/.test(t)) {
    bar = Math.min(5, bar + 1);
  }
  return bar;
};

// Candidate-side bars — mocked until profile-derived computation lands.
// Tuned to a senior-IC data person (the implicit Reef.ai-style profile in
// our demo evidence): strong tech, some leadership, Bachelor's, ~9 yrs.
const MOCK_CANDIDATE_BARS = { tech: 5, mgmt: 3, edu: 2, exp: 4 };

const computeThemeBars = (position, analysis) => {
  const reqsText = [
    ...(position?.requirements || analysis?.requirements || []),
    ...(position?.qualifications || analysis?.qualifications || []),
  ].join(' \n ');
  const role = {
    tech: techDots(position?.job_family || analysis?.job_family, reqsText),
    mgmt: mgmtDots(position?.level || analysis?.level, position?.experience_level || analysis?.experience_level, reqsText),
    edu:  eduDots(reqsText),
    exp:  expDots(position?.level || analysis?.level, position?.experience_level || analysis?.experience_level),
  };
  return [
    { id: 'tech', label: 'Tech', required: role.tech, has: MOCK_CANDIDATE_BARS.tech },
    { id: 'mgmt', label: 'Mgmt', required: role.mgmt, has: MOCK_CANDIDATE_BARS.mgmt },
    { id: 'edu',  label: 'Edu.', required: role.edu,  has: MOCK_CANDIDATE_BARS.edu },
    { id: 'exp',  label: 'Exp.', required: role.exp,  has: MOCK_CANDIDATE_BARS.exp },
  ];
};

// ---- Match status model ---------------------------------------------------
// Mock per-row match status. The row visualization itself is the shared
// RequirementRow component (target-dot model); we map status → has-dots so
// the existing MOCK_STATUS_CYCLE arrays still work unchanged.
const MATCH_STATUSES = {
  strong:  { dots: 5, label: 'Strong',  color: '#3d7a4a' },
  meets:   { dots: 4, label: 'Meets',   color: SAGE[600] },
  partial: { dots: 2, label: 'Partial', color: '#c4956a' },
  gap:     { dots: 0, label: 'Gap',     color: '#c48a8a' },
};

const MOCK_STATUS_CYCLE_REQUIRED = ['strong', 'meets', 'partial', 'partial', 'gap', 'strong', 'meets', 'partial', 'gap'];
const MOCK_STATUS_CYCLE_PREFERRED = ['meets', 'partial', 'gap', 'partial', 'meets', 'strong', 'gap'];

const MOCK_EVIDENCE = {
  strong: [
    { source: 'Reef.ai · Head of Data', dates: '2021–2024', text: 'Built and operationalized ML models — churn, utilization, lead scoring — directly inside the Reef Application', strength: 'strong' },
    { source: 'FlyHomes · Sr. Data Scientist', dates: '2019–2021', text: 'Built MLOps and ML infrastructure: model registries, feature stores, data pipelines', strength: 'strong' },
    { source: 'Remitly · Data Scientist', dates: '2017–2019', text: 'Real-time fraud evaluation model in production; prevented ~$1M annual loss', strength: 'strong' },
    { source: 'PayScale · Data Scientist', dates: '2015–2017', text: "Maintained and improved PayScale's flagship compensation model", strength: 'medium' },
  ],
  meets: [
    { source: 'Reef.ai · Head of Data', dates: '2021–2024', text: 'Worked directly with clients to understand business and data needs; architected solutions', strength: 'strong' },
    { source: 'FlyHomes · Sr. Data Scientist', dates: '2019–2021', text: 'Ran recruiting, hiring, onboarding and training for the data team', strength: 'medium' },
    { source: 'Skills · Profile', dates: '', text: 'Python, SQL, R listed as core technical skills', strength: 'medium' },
  ],
  partial: [
    { source: 'Reef.ai · Head of Data', dates: '2021–2024', text: 'Infrastructure work implies relevant tooling but accomplishments do not name it explicitly', strength: 'weak' },
    { source: 'Skills · Profile', dates: '', text: 'Listed in tools section but not mentioned in role accomplishments', strength: 'weak' },
  ],
  gap: [],
};

const formatPostedDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
};

const formatSalary = (min, max) => {
  const toK = (s) => {
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`;
  };
  const lo = toK(min);
  const hi = toK(max);
  if (lo && hi) return `${lo} – ${hi}`;
  return lo || hi || null;
};

const companyInitial = (name) => (name || '?').trim().charAt(0).toUpperCase();

const JobPostingPreviewPage = () => {
  const { companyDomain, jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('requirements');
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/postings/${companyDomain}/${jobId}`, {
      headers: { ...mockAuthHeaders() },
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message || 'Failed to load posting'))
      .finally(() => setLoading(false));
  }, [companyDomain, jobId]);

  // User preferences power the "matches your preferred markets / level"
  // comparisons in the fit strip. Best-effort — page still renders cleanly
  // (prompt-state tiles) if this fails.
  useEffect(() => {
    fetch(`${API_URL}/api/preferences`, {
      headers: { ...mockAuthHeaders() },
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => setPrefs(p || {}))
      .catch(() => setPrefs({}));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ textAlign: 'center', py: 12 }}>
        <Typography sx={{ color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
          {error ? `Posting unavailable (${error})` : 'Posting not found'}
        </Typography>
      </Box>
    );
  }

  // Prefer the unified position/company blocks from the new API response.
  // Fall back to the legacy posting/analysis fields for older payloads.
  const posting = data.posting || data.raw?.posting || {};
  const analysis = data.analysis || data.raw?.analysis || {};
  const position = data.position || {};
  const companyHeader = data.company || {};

  const title = position.title || analysis.position || posting.title || 'Untitled posting';
  const companyName = companyHeader.name || data.company_name || data.company_domain;
  const location =
    position.location ||
    analysis.location ||
    (typeof posting.location === 'object' ? posting.location?.name : posting.location) ||
    '';
  const markets = position.markets || [];
  const salary = formatSalary(
    position.salary_min || analysis.salary_min,
    position.salary_max || analysis.salary_max,
  );
  const employmentType = position.employment_type || analysis.employment_type;
  const experienceLevel = position.level || position.experience_level || analysis.experience_level;
  const sourceUrl =
    position.application_url || analysis.application_url || posting.absolute_url || posting.url || null;
  const postedAgo = formatPostedDate(position.posted_at || posting.updated_at || posting.created_at);
  const description = analysis.description || analysis.summary || '';
  const requirements = position.requirements?.length ? position.requirements : (analysis.requirements || []);
  const qualifications = position.qualifications?.length ? position.qualifications : (analysis.qualifications || []);
  const responsibilities = position.responsibilities?.length ? position.responsibilities : (analysis.responsibilities || []);
  const proseSections = position.prose_sections || analysis.prose_sections || {};

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: SAGE[500] }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{ fontSize: '0.8rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", cursor: 'pointer' }}
          onClick={() => navigate(-1)}
        >
          Back
        </Typography>
        <Typography sx={{ color: SAGE[300], fontSize: '0.8rem' }}>/</Typography>
        <Typography
          sx={{ fontSize: '0.8rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", cursor: 'pointer' }}
          onClick={() => navigate(`/explore/companies/${companyDomain}`)}
        >
          {companyName}
        </Typography>
      </Box>

      {/* Header card */}
      <Box
        sx={{
          bgcolor: CREAM[50],
          border: `2px solid ${SAGE[200]}`,
          borderRadius: 3,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          ml: 6,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
          {/* Logo placeholder — first letter of company */}
          <Box
            sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: SAGE[100], color: SAGE[700],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '1.2rem',
              flexShrink: 0,
            }}
          >
            {companyInitial(companyName)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', fontWeight: 600, color: SAGE[700] }}
            >
              {companyName}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Outfit', sans-serif", fontWeight: 700,
                color: SAGE[900], mt: 0.25, lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>

            {/* Meta pills */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              {location && <MetaPill icon={<PlaceIcon sx={{ fontSize: 14 }} />} text={location} />}
              {salary && <MetaPill icon={<PaidIcon sx={{ fontSize: 14 }} />} text={salary} />}
              {employmentType && <MetaPill icon={<WorkOutlineIcon sx={{ fontSize: 14 }} />} text={employmentType} />}
              {experienceLevel && <MetaPill icon={<StairsIcon sx={{ fontSize: 14 }} />} text={experienceLevel} />}
              {postedAgo && (
                <Typography sx={{ fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif", alignSelf: 'center' }}>
                  · Posted {postedAgo}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
            <Button
              variant="contained"
              disabled
              sx={{
                bgcolor: SAGE[600], color: CREAM[50], textTransform: 'none',
                fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.8rem',
                borderRadius: 2, px: 2, py: 0.85,
                '&:hover': { bgcolor: SAGE[700] },
                '&.Mui-disabled': { bgcolor: SAGE[300], color: CREAM[100] },
              }}
              title="Coming with the matching revamp"
            >
              Start Application
            </Button>
            {sourceUrl && (
              <Button
                component={Link}
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: SAGE[100], color: SAGE[700], textTransform: 'none',
                  fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.78rem',
                  borderRadius: 2, px: 2, py: 0.75,
                  '&:hover': { bgcolor: SAGE[200] },
                }}
              >
                Source
              </Button>
            )}
          </Box>
        </Box>

        {description && (
          <Typography
            sx={{
              fontSize: '0.88rem', color: SAGE[600], lineHeight: 1.6,
              mt: 2.5, pt: 2.5, borderTop: `1px solid ${SAGE[100]}`,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* Fit Strip — position-level "Your fit at a glance" */}
      <Box sx={{ ml: 6, mb: 3 }}>
        <FitStrip
          salary={salary}
          location={location}
          markets={markets}
          level={experienceLevel}
          prefs={prefs}
          themeBars={computeThemeBars(position, analysis)}
        />
      </Box>

      {/* Tabs */}
      <Box sx={{ ml: 6, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: 2.5, bgcolor: `${SAGE[100]}80` }}>
          {BASE_TABS.map((tab) => {
            const label = tab.id === 'about' ? `About ${companyName}` : tab.label;
            return (
              <Box
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  flex: 1, textAlign: 'center', py: 1.1, px: 2, borderRadius: 2,
                  cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
                  fontSize: '0.85rem', fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? SAGE[800] : SAGE[400],
                  bgcolor: activeTab === tab.id ? CREAM[50] : 'transparent',
                  boxShadow: activeTab === tab.id ? `0 1px 3px ${SAGE[200]}` : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Tab content */}
      <Box sx={{ ml: 6 }}>
        {activeTab === 'requirements' && (
          <RequirementsTab requirements={requirements} qualifications={qualifications} position={position} />
        )}
        {activeTab === 'responsibilities' && (
          <ResponsibilitiesTab items={responsibilities} />
        )}
        {activeTab === 'about' && (
          <AboutTab proseSections={proseSections} companyName={companyName} companyHeader={companyHeader} />
        )}
        {activeTab === 'raw' && (
          <RawPostingTab posting={posting} sourceUrl={sourceUrl} />
        )}
      </Box>
    </Box>
  );
};

const MetaPill = ({ icon, text }) => (
  <Box
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.25, py: 0.4, borderRadius: 1.5,
      bgcolor: SAGE[100], color: SAGE[700],
      fontFamily: "'Outfit', sans-serif", fontSize: '0.75rem', fontWeight: 500,
    }}
  >
    {icon}
    {text}
  </Box>
);

const SectionCard = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: CREAM[50],
      border: `2px solid ${SAGE[200]}`,
      borderRadius: 3,
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SectionHeader = ({ label, count, accent }) => (
  <Box
    sx={{
      px: 2.5, py: 1.5,
      bgcolor: accent ? SAGE[100] : `${SAGE[100]}80`,
      borderBottom: `1px solid ${SAGE[200]}`,
      display: 'flex', alignItems: 'center',
    }}
  >
    <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.75rem', fontWeight: 700, color: SAGE[700], letterSpacing: 0.4, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    {count != null && (
      <Typography sx={{ ml: 'auto', fontSize: '0.75rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
        {count} {count === 1 ? 'item' : 'items'}
      </Typography>
    )}
  </Box>
);

const BulletRow = ({ index, text }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${SAGE[100]}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Box
      sx={{
        minWidth: 22, height: 22, borderRadius: 1, mt: 0.25,
        bgcolor: SAGE[100], color: SAGE[600],
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Outfit', sans-serif", fontSize: '0.7rem', fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {index + 1}
    </Box>
    <Typography sx={{ fontSize: '0.88rem', color: SAGE[800], lineHeight: 1.55, fontFamily: "'Outfit', sans-serif" }}>
      {text}
    </Typography>
  </Box>
);

const RequirementsTab = ({ requirements, qualifications, position }) => {
  const [expanded, setExpanded] = useState(null);
  const [scopeRow, setScopeRow] = useState(null);
  const [fillRow, setFillRow] = useState(null);
  const hasAny = requirements.length > 0 || qualifications.length > 0;
  if (!hasAny) {
    return (
      <SectionCard sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem' }}>
          No structured requirements extracted for this posting yet.
        </Typography>
      </SectionCard>
    );
  }

  const toggle = (key) => setExpanded(expanded === key ? null : key);

  // Open the scope modal in-place. Conversation creation is owned by the
  // modal itself — no navigation, no separate /enhancement page roundtrip.
  const openScope = (reqText, status) => {
    setScopeRow({
      text: reqText,
      target: 5,
      has: MATCH_STATUSES[status]?.dots ?? 0,
      evidence: MOCK_EVIDENCE[status] || [],
    });
  };
  const openFill = (reqText, status) => {
    setFillRow({
      text: reqText,
      target: 5,
      has: MATCH_STATUSES[status]?.dots ?? 0,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.72rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
          Per-requirement evidence shown here is sample data — real profile matching ships with the next enrichment pass.
        </Typography>
        <Box
          sx={{
            px: 1, py: 0.25, borderRadius: 99,
            bgcolor: '#c4956a20', color: '#c4956a',
            fontFamily: "'Outfit', sans-serif", fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          Sample match
        </Box>
      </Box>
      {requirements.length > 0 && (
        <SectionCard>
          <SectionHeader label="Required" count={requirements.length} accent />
          {requirements.map((r, i) => {
            const key = `req-${i}`;
            const status = MOCK_STATUS_CYCLE_REQUIRED[i % MOCK_STATUS_CYCLE_REQUIRED.length];
            return (
              <RequirementRow
                key={key}
                index={i}
                text={r}
                target={5}
                has={MATCH_STATUSES[status].dots}
                evidence={MOCK_EVIDENCE[status]}
                expanded={expanded === key}
                onToggle={() => toggle(key)}
                onIHaveThis={() => openScope(r, status)}
                onIDontHaveThis={() => openFill(r, status)}
              />
            );
          })}
        </SectionCard>
      )}
      {qualifications.length > 0 && (
        <SectionCard>
          <SectionHeader label="Preferred" count={qualifications.length} />
          {qualifications.map((r, i) => {
            const key = `pref-${i}`;
            const status = MOCK_STATUS_CYCLE_PREFERRED[i % MOCK_STATUS_CYCLE_PREFERRED.length];
            return (
              <RequirementRow
                key={key}
                index={i}
                text={r}
                target={5}
                has={MATCH_STATUSES[status].dots}
                evidence={MOCK_EVIDENCE[status]}
                expanded={expanded === key}
                onToggle={() => toggle(key)}
                onIHaveThis={() => openScope(r, status)}
                onIDontHaveThis={() => openFill(r, status)}
              />
            );
          })}
        </SectionCard>
      )}

      <ScopeBulletsModal
        open={!!scopeRow}
        onClose={() => setScopeRow(null)}
        requirement={scopeRow}
        roleContext={{
          role: position?.title || position?.job_family,
          level: position?.company_name,
        }}
      />
      <FillTheGapModal
        open={!!fillRow}
        onClose={() => setFillRow(null)}
        requirement={fillRow}
        roleContext={{
          role: position?.title || position?.job_family,
          level: position?.company_name,
        }}
      />
    </Box>
  );
};

const MatchSummaryBar = ({ requiredCount, preferredCount }) => (
  <Box
    sx={{
      bgcolor: CREAM[50],
      border: `2px solid ${SAGE[200]}`,
      borderRadius: 2.5,
      p: 2.25,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 38, height: 38, borderRadius: 2,
        bgcolor: SAGE[100], color: SAGE[600],
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <AutoGraphIcon sx={{ fontSize: 20 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '0.88rem', color: SAGE[800] }}>
        Profile Match
      </Typography>
      <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.75rem', color: SAGE[400], mt: 0.25 }}>
        {requiredCount} required · {preferredCount} preferred · per-requirement evidence rolling out next
      </Typography>
    </Box>
    <Box
      sx={{
        px: 1.5, py: 0.5, borderRadius: 99,
        bgcolor: SAGE[100], color: SAGE[600],
        fontFamily: "'Outfit', sans-serif", fontSize: '0.7rem', fontWeight: 600,
        flexShrink: 0,
      }}
    >
      Preview
    </Box>
  </Box>
);

const ResponsibilitiesTab = ({ items }) => {
  if (!items.length) {
    return (
      <SectionCard sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem' }}>
          No responsibilities extracted for this posting yet.
        </Typography>
      </SectionCard>
    );
  }
  return (
    <SectionCard>
      <SectionHeader label="What you'll do" count={items.length} accent />
      {items.map((r, i) => <BulletRow key={i} index={i} text={r} />)}
    </SectionCard>
  );
};

// ---- Fit Strip (position-level) ------------------------------------------
// Mirrors CompanyInsightsPage's "Your fit at a glance" tile grid, but each
// tile reflects THIS posting (not an aggregation). When the user has no
// preference set for a dimension, the tile drops into a sage prompt state.
const FitTile = ({ label, headlineStat, headlineSub, footer, children }) => (
  <Box
    sx={{
      borderRadius: 2,
      bgcolor: CREAM[50],
      border: `1.5px solid ${SAGE[200]}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ p: 2, flex: 1 }}>
      <Typography
        sx={{
          fontSize: '0.62rem', fontWeight: 700, color: SAGE[400],
          letterSpacing: 0.5, textTransform: 'uppercase',
          fontFamily: "'Outfit', sans-serif", mb: 1,
        }}
      >
        {label}
      </Typography>
      {children ? (
        children
      ) : (
        <>
          <Typography
            sx={{
              fontSize: '1.15rem', fontWeight: 700, color: SAGE[800],
              fontFamily: "'Outfit', sans-serif", lineHeight: 1.2,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {headlineStat || '—'}
          </Typography>
          {headlineSub && (
            <Typography sx={{ fontSize: '0.72rem', color: SAGE[500], fontFamily: "'Outfit', sans-serif", mt: 0.5 }}>
              {headlineSub}
            </Typography>
          )}
        </>
      )}
    </Box>
    <Box
      sx={{
        px: 2, py: 1,
        borderTop: `1px solid ${footer.matches ? SAGE[200] : '#c4956a25'}`,
        bgcolor: footer.matches ? `${SAGE[100]}70` : '#c4956a12',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.7rem', fontWeight: 600,
          color: footer.matches ? SAGE[700] : '#c4956a',
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        {footer.label}
      </Typography>
      {footer.detail && (
        <Typography
          sx={{
            fontSize: '0.65rem',
            color: footer.matches ? SAGE[500] : '#c4956a99',
            fontFamily: "'Outfit', sans-serif",
            mt: 0.25,
          }}
        >
          {footer.detail}
        </Typography>
      )}
    </Box>
  </Box>
);

const FitStrip = ({ salary, location, markets, level, prefs, themeBars }) => {
  const userMarkets = prefs?.markets || [];
  const userLevels = prefs?.levels || [];

  // ---- Comp tile — no user comp target wired yet, always show prompt ----
  const compFooter = salary
    ? { matches: false, label: 'Set target comp', detail: "We'll compare against your range" }
    : { matches: false, label: 'Salary not listed', detail: 'Bring this up early in the process' };

  // ---- Location tile — compare posting market(s) vs user pref markets ----
  const marketMatch = markets.length && userMarkets.length
    ? markets.some((m) => userMarkets.includes(m))
    : null;
  const locationFooter = userMarkets.length === 0
    ? { matches: false, label: 'Set your markets', detail: "We'll show market fit" }
    : marketMatch
      ? { matches: true, label: '✓ In your preferred markets', detail: markets.slice(0, 2).join(' · ') }
      : { matches: false, label: 'Outside your preferred markets', detail: markets.slice(0, 2).join(' · ') || '—' };

  // ---- Level tile — compare posting level vs user pref levels ----
  const levelMatch = level && userLevels.length
    ? userLevels.some((l) => l.toLowerCase() === String(level).toLowerCase())
    : null;
  const levelFooter = userLevels.length === 0
    ? { matches: false, label: 'Set your levels', detail: "We'll show level fit" }
    : levelMatch
      ? { matches: true, label: '✓ Matches your level', detail: null }
      : { matches: false, label: 'Outside your level range', detail: null };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1, px: 0.5 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: SAGE[800], fontFamily: "'Outfit', sans-serif" }}>
          Your fit at a glance
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}>
          For this position
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1.5,
        }}
      >
        <FitTile
          label="Comp"
          headlineStat={salary || 'Not listed'}
          headlineSub={salary ? 'Listed band' : null}
          footer={compFooter}
        />
        <FitTile
          label="Location"
          headlineStat={location || '—'}
          headlineSub={markets.length ? `${markets.length} market${markets.length === 1 ? '' : 's'}` : null}
          footer={locationFooter}
        />
        <FitTile
          label="Level"
          headlineStat={level || '—'}
          headlineSub={null}
          footer={levelFooter}
        />
        <FitTile
          label="Match"
          footer={{ matches: true, label: 'Role bar vs your level', detail: 'Sample candidate — real profile read ships next' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {(themeBars || []).map((t) => (
              <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: SAGE[600],
                    fontWeight: 600,
                    fontFamily: "'Outfit', sans-serif",
                    minWidth: 32,
                  }}
                >
                  {t.label}
                </Typography>
                <TargetDots required={t.required} has={t.has} />
              </Box>
            ))}
          </Box>
        </FitTile>
      </Box>
    </Box>
  );
};

// ---- About Tab — stacked prose sections + light company firmographics ----
const AboutSection = ({ title, body }) => (
  <SectionCard sx={{ p: 2.75 }}>
    <Typography
      sx={{
        fontSize: '0.7rem', fontWeight: 700, color: SAGE[500],
        letterSpacing: 0.5, textTransform: 'uppercase',
        fontFamily: "'Outfit', sans-serif", mb: 1.25,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.88rem', color: SAGE[700], lineHeight: 1.65,
        fontFamily: "'Outfit', sans-serif", whiteSpace: 'pre-line',
      }}
    >
      {body}
    </Typography>
  </SectionCard>
);

const AboutTab = ({ proseSections, companyName, companyHeader }) => {
  const sections = [
    { key: 'about_company', title: `About ${companyName}` },
    { key: 'about_team', title: 'About the team' },
    { key: 'about_role', title: 'About the role' },
    { key: 'benefits', title: 'Benefits' },
    { key: 'eeo_statement', title: 'Equal opportunity' },
  ];
  const present = sections.filter((s) => proseSections?.[s.key]);

  const firmographics = [
    companyHeader?.headquarters,
    companyHeader?.industry,
    companyHeader?.size_estimate,
  ].filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Company firmographics — quick reference card */}
      {(companyHeader?.tagline || firmographics.length > 0) && (
        <SectionCard sx={{ p: 2.5 }}>
          {companyHeader?.tagline && (
            <Typography
              sx={{
                fontSize: '0.95rem', fontWeight: 500, color: SAGE[800],
                fontFamily: "'Outfit', sans-serif", fontStyle: 'italic',
                lineHeight: 1.5, mb: firmographics.length > 0 ? 1.5 : 0,
              }}
            >
              “{companyHeader.tagline}”
            </Typography>
          )}
          {firmographics.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {firmographics.map((f, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 1.25, py: 0.4, borderRadius: 1.5,
                    bgcolor: SAGE[100], color: SAGE[700],
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.75rem', fontWeight: 500,
                  }}
                >
                  {f}
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      )}

      {present.length > 0 ? (
        present.map((s) => (
          <AboutSection key={s.key} title={s.title} body={proseSections[s.key]} />
        ))
      ) : (
        <SectionCard sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem' }}>
            No narrative sections extracted for this posting yet. The Full Posting tab has the raw body.
          </Typography>
        </SectionCard>
      )}
    </Box>
  );
};

const RawPostingTab = ({ posting, sourceUrl }) => {
  const body = posting?.content || posting?.body || posting?.description || '';
  return (
    <SectionCard sx={{ p: 3 }}>
      {sourceUrl && (
        <Box sx={{ mb: 2 }}>
          <Link
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', color: SAGE[600],
              fontWeight: 500, textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View original posting <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      )}
      {body ? (
        <Typography
          component="div"
          sx={{
            fontSize: '0.85rem', color: SAGE[700], lineHeight: 1.7,
            whiteSpace: 'pre-wrap', fontFamily: "'Outfit', sans-serif",
          }}
          dangerouslySetInnerHTML={{ __html: body }}
        />
      ) : (
        <Typography sx={{ color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem' }}>
          Full posting body not cached for this listing — use the source link above.
        </Typography>
      )}
    </SectionCard>
  );
};

export default JobPostingPreviewPage;
